import React, { useState, useEffect, useRef } from "react";
import { formatPrice, generateID, isValidateEmail } from '../helpers/CommonHelpers';
import { useNavigate } from "react-router-dom";
import { PersonList, EmptyCartHTML, AllreadyHaveAccountHTML, deleteDBSession, ConfirmPopup, CartFeaturedEventItem, CartFeaturedPlanItem, calculateGrandTotal, OrderSummary, PayerInformation, PaymentInformation, InvoiceForm, PrepaidTicket, BackToCartButton } from "../components/ReusableComponents";
import { cartToItems, getCurrency, pushEcommEvent } from "../utils/ga4";


const CheckoutAndPayment = () => {
    const im_custom = window.im_ecom || {};
    const navigate = useNavigate();
    const plans = (im_custom?.plans && Array.isArray(im_custom.plans)) ? im_custom.plans : [];
    const events = (im_custom?.events && Array.isArray(im_custom.events)) ? im_custom.events : [];

    //  Modal visibility
    const [showModal, setShowModal] = useState(false)
    const [grandTotal, setGrandTotal] = useState(0);
    const [estimatedTax, setEstimatedTax] = useState(0);
    const [highlightAttendeeError, setHighlightAttendeeError] = useState(false);

    const [modalData, setModalData] = useState({
        title: "",
        message: "",
        action: null,
    });

    const userAgreedRef = useRef(null);
    const attendeeAgreedRef = useRef(null);
    const paymentInformationRef = useRef(null);

    // Track if cart has been initialized (loaded from localStorage or has content)
    const isInitializedRef = useRef(false);

    const newPerson = (price, isDefault = "1", type = "user",) => ({
        'id': generateID(type === "user" ? "user_" : "attendee_"),
        'firstName': "",
        'lastName': "",
        'email': "",
        'confirmEmail': "",
        'label': "Additional " + (type === "user" ? " User" : " Attendee"),
        'price': price,
        'isDefault': isDefault,
        'leaveSeatUnassigned': type === "attendee" ? "0" : "1",  // unchecked for attendee 
        'openUserForm': type === "attendee" ? "1" : 0,        // attendee form open
        'errors': {},
    });
    const [userCart, setUserCart] = useState({
        users: [],
        attendees: [],
        activePlan: null,
        activeEvent: null,
        isAgreed: "0",
        isUserAgreed: "0",
        highlightUserAgreed: false,
        isAttendeeAgreed: "0",
        highlightAttendeeAgreed: false,
        paymentMethod: "credit_card",
        paymentMethodInfo: {
            name: im_custom.user.name,
            email: im_custom.user.email,
        },
        sessionID: null,
    });


    const validatePerson = (p) => {
        // let errors = {};
        // if (!p.firstName) errors.firstName = "First name required";
        // if (!p.lastName) errors.lastName = "Last name required";
        // if (!p.email) errors.email = "Email required";
        // if (p.email && !isValidateEmail(p.email)) errors.email = "Invalid email address";
        // if (!p.confirmEmail) errors.confirmEmail = "Confirm email required";
        // if (p.email !== p.confirmEmail) errors.confirmEmail = "Emails do not match";
        // return errors;
    };

    const handleBacktoCart = () => {
        navigate("/cart");
    };

    useEffect(() => {
        const saved = localStorage.getItem("user_cart");
        const savedParse = saved ? JSON.parse(saved) : null;
        if (savedParse) {
            console.log("Loaded saved cart:", savedParse);
            // Ensure paymentMethod defaults to "credit_card" if missing or invalid
            if (!savedParse.paymentMethod || (savedParse.paymentMethod !== "credit_card" && savedParse.paymentMethod !== "invoice" && savedParse.paymentMethod !== "prepaid-ticket")) {
                savedParse.paymentMethod = "credit_card";
            }
            setUserCart(savedParse);
            // Mark as initialized if cart has content
            if (savedParse.activePlan || savedParse.activeEvent) {
                isInitializedRef.current = true;
            }
        }
    }, []);

    /* first attendee's fields if empty, prefill purchaser If info is available*/
    useEffect(() => {
        if (!im_custom.is_logged_in || !im_custom.user) return;

        setUserCart((p) => {
            if (!p.attendees || p.attendees.length === 0) return p;

            const first = p.attendees[0];

            if (first.firstName || first.lastName || first.email) return p;

            const userName = im_custom.user.name || "";
            const nameParts = userName.trim().split(" ");
            const firstName = im_custom.user.first_name || nameParts[0] || "";
            const lastName  = im_custom.user.last_name  || nameParts.slice(1).join(" ") || "";
            const email     = im_custom.user.email || "";

            const updatedAttendees = p.attendees.map((a, idx) => {
                if (idx !== 0) return a;
                return {
                    ...a,
                    firstName,
                    lastName,
                    email,
                    confirmEmail: "",   // prefill by user
                    openUserForm: "1",  // form open for first attendee
                    leaveSeatUnassigned: "0",
                };
            });

            return { ...p, attendees: updatedAttendees };
        });
    }, [im_custom.is_logged_in, im_custom.user?.email]);

    useEffect(() => {
        // console.log("User cart changed:", userCart);
        setGrandTotal(calculateGrandTotal(userCart, im_custom));

        // Mark as initialized when cart gets content
        if (!isInitializedRef.current && (userCart.activePlan || userCart.activeEvent)) {
            isInitializedRef.current = true;
        }

        // Only persist if cart is initialized and has content
        if (!isInitializedRef.current) return;
        if (!userCart.activePlan && !userCart.activeEvent) return;

        localStorage.setItem("user_cart", JSON.stringify(userCart));
    }, [userCart]);

    // GA4: begin_checkout on cart changes
    useEffect(() => {
        if (!userCart.activePlan && !userCart.activeEvent) return;
        const currency = getCurrency(im_custom);
        const value = calculateGrandTotal(userCart, im_custom);
        const items = cartToItems(userCart, im_custom);
        pushEcommEvent('begin_checkout', { currency, value, items });
    }, [userCart.activePlan, userCart.activeEvent, userCart.users.length, userCart.attendees.length]);

    const changeActiveEvent = (event) => {
        setUserCart((p) => ({
            ...p,
            activeEvent: event,
            // Reset users and attendees based on new plan),
        }));
    }

    const addEventToCart = () => {
        const userName = im_custom.user?.name || "";
        const nameParts = userName.trim().split(" ");
        const firstName = im_custom.user?.first_name || nameParts[0] || "";
        const lastName = im_custom.user?.last_name || nameParts.slice(1).join(" ") || "";

        setUserCart((p) => ({
            ...p,
            attendees: [...p.attendees, {
                ...newPerson(im_custom.event_price.price, "1", "attendee"),
                firstName: firstName,       // purchaser first name auto-fill
                lastName: lastName,         // purchaser  last name auto-fill
                email: im_custom.user?.email || "",  // purchaser email auto-fill
                confirmEmail: "",           // confirm email empty
                leaveSeatUnassigned: "0",   // checkbox unchecked
                openUserForm: "1",          // form open
            }],
        }));
    }

    const addUser = () => setUserCart((p) => ({
        ...p,
        users: [...p.users, {
            ...newPerson(userCart.activePlan.extra_atlas_user_price, "0", "user"),
            leaveSeatUnassigned: "0",  // unchecked
            openUserForm: "1",          // form open
        }]
    }));
    const removeUser = (id) => setUserCart((p) => ({ ...p, users: p.users.filter((u) => u.id !== id) }));
    const updateUser = (id, updates) => {
        setUserCart((p) => ({
            ...p,
            users: p.users.map((u) => {
                if (u.id !== id) return u;
                const updated = { ...u, ...updates };
                return { ...updated, errors: validatePerson(updated) };
            }),
        }));
    };

    const addAttendee = () => setUserCart((p) => ({
    ...p,
    attendees: [...p.attendees, {
        ...newPerson(im_custom.event_price.extra_training_tickets_price, "0", "attendee"),
        leaveSeatUnassigned: "0",  // unchecked
        openUserForm: "1",          // form open
    }]
}));
    const removeAttendee = (id) => setUserCart((p) => ({ ...p, attendees: p.attendees.filter((a) => a.id !== id) }));
    const updateAttendee = (id, updates) => {
        setUserCart((p) => ({
            ...p,
            attendees: p.attendees.map((a) => {
                if (a.id !== id) return a;
                const updated = { ...a, ...updates };
                return { ...updated, errors: validatePerson(updated) };
            }),
        }));
    };

    const handleCompletePurchase = () => {
        const unfilledAttendee = userCart.attendees.find(
            (a) => !a.firstName || !a.lastName || !a.email || !a.confirmEmail || a.email !== a.confirmEmail
        );
        if (unfilledAttendee) {
            updateAttendee(unfilledAttendee.id, { openUserForm: "1" });
            setHighlightAttendeeError(true);
            setTimeout(() => setHighlightAttendeeError(false), 2000);
            const el = document.getElementById(`attendee-${unfilledAttendee.id}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        if (userCart.users.length > 0 && userCart.isUserAgreed == "0") {
            setUserCart((p) => ({
                ...p,
                highlightUserAgreed: true
            }));
            userAgreedRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center"
            });
            setTimeout(() => {
                setUserCart((p) => ({
                    ...p,
                    highlightUserAgreed: false
                }));
            }, 2000);
            return;
        }
        if (userCart.attendees.length > 0 && userCart.isAttendeeAgreed == "0") {
            setUserCart((p) => ({
                ...p,
                highlightAttendeeAgreed: true
            }));
            attendeeAgreedRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center"
            });
            setTimeout(() => {
                setUserCart((p) => ({
                    ...p,
                    highlightAttendeeAgreed: false
                }));
            }, 2000);
            return;
        }


        paymentInformationRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center"
        });

        if (!im_custom.is_logged_in) {
            window.location.href = "/login?redirect_to=/checkout_and_payment";
            return;
        }
    };

    /* Remove entire cart */
    const handleRemoveCart = () => {
        // delete only plan if there is no training tickets but attendees
        if (userCart.activePlan.training_tickets === 0 && userCart.attendees.length > 0) {
            const updatedCart = { ...userCart, activePlan: null, users: [], isUserAgreed: "0", highlightUserAgreed: false };
            setUserCart(updatedCart);
            localStorage.setItem("user_cart", JSON.stringify(updatedCart));
        } else {
            if (userCart.sessionID) {
                deleteDBSession(userCart.sessionID);
            }
            const updatedCart = {
                ...userCart,
                users: [],
                attendees: [],
                activePlan: null,
                activeEvent: null,
                isAgreed: "0",
                sessionID: null,
                isUserAgreed: "0",
                highlightUserAgreed: false,
                isAttendeeAgreed: "0",
                highlightAttendeeAgreed: false,
            };
            setUserCart(updatedCart);
            isInitializedRef.current = false;
            localStorage.removeItem("user_cart");
        }
        setShowModal(false);
    };

    /* Remove event from cart */
    const handleRemoveEvent = (e) => {
        e.preventDefault();
        const updatedCart = { ...userCart, activeEvent: null, attendees: [] };
        setUserCart(updatedCart);
        localStorage.setItem("user_cart", JSON.stringify(updatedCart));
        setShowModal(false);
    }

    /* If cart is empty, show empty cart message */
    if (userCart.users.length == 0 && userCart.attendees.length == 0) {
        return (<EmptyCartHTML />);
    }

    return (
        <div className="about-wrapper">
            <p className={`i-agree-error-flag ${userCart.highlightUserAgreed ? "shake-text" : ""}`}>Please check this box to allow all members to receive emails from Directions on Microsoft.</p>
            <p className={`i-agree-error-flag ${userCart.highlightAttendeeAgreed ? "shake-text" : ""}`}>Please check this box to allow all training session attendees to receive emails from Directions on Microsoft.</p>
            <p className={`i-agree-error-flag ${highlightAttendeeError ? "shake-text" : ""}`}>Please fill in all attendee details.</p>
            <div className="about-container cart-main">
                < BackToCartButton onClick={handleBacktoCart} />
                <div className="row justify-content-space-between">
                    <div className="col-md-8">
                        <h1>Checkout & Payment</h1>
                        {!im_custom.is_logged_in && (<AllreadyHaveAccountHTML />)}

                        {/* Standard Plan */}
                        {userCart.activePlan && (
                            <>
                                <div className="standard">
                                    <div>
                                        <h3>{userCart.activePlan.plan_label}</h3>
                                        <p>{userCart.activePlan.atlas_users} Atlas
                                            memberships{userCart.activePlan.training_tickets > 0 && `, ${userCart.activePlan.training_tickets} Microsoft Licensing Training`}</p>
                                    </div>
                                    <div className="price">
                                        <a
                                            href="#"
                                            className="custom-secondary-btn text-decoration-none"
                                            onClick={() =>
                                                setModalData({
                                                    title: "Remove this plan?",
                                                    message:
                                                        "All users will be removed. Are you sure you want to remove this plan from your cart?",
                                                    action: handleRemoveCart,
                                                }) || setShowModal(true)
                                            }
                                        >
                                            Remove Plan
                                        </a>
                                        <div>{userCart.activePlan ? `${formatPrice(userCart.activePlan.price)}` : ""}</div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Research 1 — Atlas Membership */}

                        {userCart.activePlan && (
                            <div className="research">
                                <div className="row">
                                    <div className="col-2">
                                        <div className="image-wrap">
                                            <img src="/wp-content/themes/dom/assets/img/atlas-laptop.webp"
                                                alt="Atlas Membership" />
                                        </div>
                                    </div>
                                    <div className="col-10">
                                        <div className="research-one">
                                            <div>
                                                <h3>Atlas Membership</h3>
                                                <p>
                                                    Online 12 Month Subscription access to unbiased analyses, roadmaps, decision kits, infographics, reference guides and more. Atlas simplifies Microsoft for your enterprise. Available 24/7 via desktop, tablet and mobile.
                                                </p>
                                            </div>
                                            {/*<div className="research-text">research</div>*/}
                                        </div>
                                        {/* USERS */}
                                        <PersonList
                                            people={userCart.users}
                                            addPerson={addUser}
                                            removePerson={removeUser}
                                            updatePerson={updateUser}
                                            options={{
                                                extraPersonPrice: userCart.activePlan.extra_atlas_user_price,
                                                typeLabel: "User",
                                                type: 'users'
                                            }} />
                                        <label style={{ lineHeight: "30px" }} className={`position-relative ps-4 ${userCart.highlightUserAgreed ? "shake-text" : ""}`} >
                                            <input
                                                type="checkbox"
                                                ref={userAgreedRef}
                                                className="filter-checkbox position-absolute"
                                                style={{ left: 0 }}
                                                checked={userCart.isUserAgreed == "1" ? true : false}
                                                onChange={(e) => {
                                                    setUserCart((p) => ({
                                                        ...p,
                                                        isUserAgreed: e.target.checked ? "1" : "0"
                                                    }));
                                                }}
                                            />
                                            I agree for all members to receive emails from Directions on Microsoft.
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Research 2 — Microsoft Licensing Training */}
                        {userCart.attendees.length > 0 && (
                            <>
                                {(!userCart.activePlan || userCart.activePlan.training_tickets == 0) && (
                                    <div className="standard">
                                        <div>
                                            <h3>Event</h3>
                                            <p>Microsoft Licensing Training</p>
                                        </div>

                                        <div className="price">
                                            <a
                                                href="#"
                                                className="custom-secondary-btn text-decoration-none"
                                                onClick={(e) =>
                                                    setModalData({
                                                        title: "Remove this event?",
                                                        message:
                                                            "All attendees will be removed. Are you sure you want to remove this event from your cart?",
                                                        action: () => handleRemoveEvent(e),
                                                    }) || setShowModal(true)
                                                }
                                            >
                                                Remove Event
                                            </a>
                                            <div>{im_custom.event_price ? `${formatPrice(im_custom.event_price.price)}` : ""}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="research">
                                    <div className="row">
                                        <div className="col-2">
                                            <div className="image-wrap">
                                                <img src="/wp-content/themes/dom/assets/img/LBC-ticket-thumbnail.png"
                                                    alt="Microsoft Licensing Training" />
                                            </div>
                                        </div>
                                        <div className="col-10">
                                            <div className="research-one">
                                                <div>
                                                    <h3>Microsoft Licensing Training</h3>
                                                    <p>
                                                        18+ hour intensive training and professional certification is the most current and complete training on Microsoft licensing available.  Learn a disciplined process and proven negotiation strategies for optimizing your next contract negotiation.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="analyst-reports-filter" style={{ position: "relative" }}>
                                                <select className="form-control border-select-control lbc-drop-down" aria-label="Select a Course" id="courseDropdown"
                                                    value={userCart.activeEvent?.event_id || ""}
                                                    onChange={(e) => {
                                                        const selectedEvent = events.find(event => event.event_id.toString() === e.target.value);
                                                        changeActiveEvent(selectedEvent || null);
                                                    }}
                                                >
                                                    <option value="">Select a Course or Choose Later</option>

                                                    {events.length > 0 ? (
                                                        events.map((event) => {
                                                            // Format date range like "NovF 10-12, 2025"
                                                            const start = event.start_date ? new Date(event.start_date) : null;
                                                            const end = event.end_date ? new Date(event.end_date) : null;

                                                            const formattedDate = start
                                                                ? `${start.toLocaleString("en-US", {
                                                                    month: "short",
                                                                    day: "numeric",
                                                                })}-${end ? `${end.getDate()}, ${end.getFullYear()}` : start.getFullYear()}`
                                                                : "";

                                                            // If no city, show Virtual
                                                            const locationText = event.city ? event.city : "Virtual";

                                                            return (
                                                                <option
                                                                    key={event.event_id}
                                                                    value={event.event_id}
                                                                >
                                                                    {`${formattedDate}: ${locationText}`}
                                                                </option>
                                                            );
                                                        })
                                                    ) : (
                                                        <option disabled>No active courses</option>
                                                    )}
                                                </select>
                                            </div>

                                            {/* ATTENDEES */}
                                            <PersonList
                                                people={userCart.attendees}
                                                addPerson={addAttendee}
                                                removePerson={removeAttendee}
                                                updatePerson={updateAttendee}
                                                options={{
                                                    extraPersonPrice: im_custom.event_price.extra_training_tickets_price,
                                                    typeLabel: "Attendee",
                                                    type: 'attendees'
                                                }} />
                                            <label style={{ lineHeight: "30px" }} className={`position-relative ps-4 ${userCart.highlightAttendeeAgreed ? "shake-text" : ""}`} >
                                                <input
                                                    type="checkbox"
                                                    ref={attendeeAgreedRef}
                                                    className="filter-checkbox position-absolute"
                                                    style={{ left: 0 }}
                                                    checked={userCart.isAttendeeAgreed == "1" ? true : false}
                                                    onChange={(e) => {
                                                        setUserCart((p) => ({
                                                            ...p,
                                                            isAttendeeAgreed: e.target.checked ? "1" : "0"
                                                        }));
                                                    }}
                                                />
                                                I agree for all attendees for this training session to receive emails from Directions on Microsoft.
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        {/*  Show Microsoft Training section only when there are NO attendees in the list */}
                        {userCart.attendees.length === 0 && (<CartFeaturedEventItem im_custom={im_custom} addToCart={addEventToCart} />)}
                        {/*  Show Microsoft Training section only when there are NO plans in the list */}
                        {!userCart.activePlan && (<CartFeaturedPlanItem im_custom={im_custom} />)}

                    </div>

                    {/*  Dynamic Order Summary */}
                    <div className="col-xxl-3 col-md-4">
                        <OrderSummary userCart={userCart} im_custom={im_custom} grandTotal={grandTotal} continueButtonText={'Complete Purchase'} continueButtonHandle={handleCompletePurchase} estimatedTax={estimatedTax} />
                        <div className="order-summary mt-3 text-center">
                            <h3>Have questions?</h3>
                            <a href="/form/?form_id=18&campaign=Checkout_And_Payment_Contact_Us" className="custom-button w-100">
                                Contact Us
                            </a>
                        </div>
                    </div>
                </div>

                <div className="payment-information" id="complete-purchase">
                    <PayerInformation im_custom={im_custom} />
                    <div className="payment-information-card">
                        <h3 className="payment-information-heading" ref={paymentInformationRef}>Payment Information</h3>
                        <div className="form-group">
                            <span>Payment Method</span>
                            <div className="d-flex flex-wrap">
                                <label className="radio-btn">
                                    <input type="radio" className="radio-button" name="paymentMethod" value="credit_card" checked={userCart.paymentMethod === "credit_card"} onChange={(e) => {
                                        setUserCart((p) => ({
                                            ...p,
                                            paymentMethod: 'credit_card'
                                        }));
                                    }} />{" "} Credit Card, ACH
                                </label>
                                <label className="radio-btn">
                                    <input type="radio" className="radio-button" name="paymentMethod" value="invoice" checked={userCart.paymentMethod === "invoice"} onChange={(e) => {
                                        setUserCart((p) => ({
                                            ...p,
                                            paymentMethod: 'invoice'
                                        }));
                                    }} />{" "} Invoice
                                </label>
                                {userCart.users.length === 0 && (<label className="radio-btn">
                                    <input type="radio" className="radio-button" name="paymentMethod" value="prepaid-ticket" checked={userCart.paymentMethod === "prepaid-ticket"} onChange={(e) => {
                                        setUserCart((p) => ({
                                            ...p,
                                            paymentMethod: 'prepaid-ticket'
                                        }));
                                    }} />{" "} Prepaid Ticket
                                </label>)}
                            </div>
                        </div>
                        {userCart.paymentMethod === 'credit_card' && grandTotal > 0 && <PaymentInformation im_custom={im_custom} userCart={userCart} grandTotal={grandTotal} onTaxUpdate={setEstimatedTax} onPaymentMethodInfoUpdate={(info) => setUserCart((p) => ({ ...p, paymentMethodInfo: info }))} />}
                        {userCart.paymentMethod === 'invoice' && <InvoiceForm im_custom={im_custom} userCart={userCart} onPaymentMethodInfoUpdate={(info) => setUserCart((p) => ({ ...p, paymentMethodInfo: info }))} />}
                        {userCart.paymentMethod === 'prepaid-ticket' && <PrepaidTicket im_custom={im_custom} userCart={userCart} onPaymentMethodInfoUpdate={(info) => setUserCart((p) => ({ ...p, paymentMethodInfo: info }))} />}
                    </div>
                </div>
            </div>

            {/*  Remove Modal */}
            {showModal && (
                <ConfirmPopup
                    title={modalData.title}
                    message={modalData.message}
                    onConfirm={modalData.action}
                    onCancel={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default CheckoutAndPayment;
